<template>
  <aha-modal
    v-if="isModalVisible"
    :classesModal="['select-modal-upload']"
    @mouseup.stop
    :title="$i18nPresenter.t('Import')"
    @hide="hide()"
    classes="mb-6"
    size="md"
    :hideHeader="false"
    :visibleOverflow="true"
    :noCloseOnBackdrop="false"
    :closeButtonTooltipProps="closeButtonTooltipProps"
  >
    <!-- File Display Section -->
    <div class="file-display-section">
      <UploadListItem
        v-if="displayedFileName"
        :filename="displayedFileName"
        :status="fileStatus"
        :error-message="aiChatbotImportSlidesError"
        @remove="removeFile"
      />
    </div>

    <!-- Action Selection Section -->
    <div class="mb-3">
      <h3 class="action-title">
        {{ $i18nPresenter.t('What do you want to do with your file?') }}
      </h3>

      <div class="action-options">
        <div
          v-for="option in actionOptions"
          :key="option.value"
          @keydown.enter="selectAction(option.value)"
          @keydown.space="selectAction(option.value)"
        >
          <aha-radio
            class="pt-2 pb-2 action-option"
            :value="option.value"
            v-model="selectedAction"
            @change="selectAction"
            v-aha-emit-action="{'importOption': option.value}"
            name="action-options"
            tabIndex="0"
            :aria-selected="selectedAction === option.value ? 'true' : 'false'"
          >
            <div class="option-content">
              <div>
                {{ $i18nPresenter.t(option.value) }}
              </div>
              <div
                class="option-icon pl-2 inline-flex"
                v-aha-tooltip="{ arrow: true, content: $i18nPresenter.t(option.tooltip) }"
                name="import-modal-v2-action-tooltip"
                v-aha-emit-action="{'import_action': option.value}"
              >
                <aha-icon name="question-v2" />
              </div>
            </div>
          </aha-radio>
        </div>
      </div>
    </div>

    <!-- Footer Section -->
    <div class="modal-footer">
      <div class="terms-disclaimer text-gray-70 pr-4">
        {{ $i18nPresenter.t('By proceeding, you agree to our') }}
        <a href="https://ahaslides.com/terms/" class="terms-link">{{ $i18nPresenter.t('Terms') }}</a> {{
          $i18nPresenter.t('and') }}
        <a href="https://ahaslides.com/privacy/" class="privacy-link">
          {{ $i18nPresenter.t('Privacy Policy') }}</a>.
      </div>
      <aha-antd-button
        v-aha-emit-action="{ 'importOption': selectedImportOption }"
        name="select-modal-upload-start-button"
        class="start-button"
        @click="onStartButtonClick"
        :disabled="!selectedImportOption || !aiChatbotUploadedFile || isUploadingFile"
        :aria-busy="isUploadingFile ? 'true' : 'false'"
      >
        {{ $i18nPresenter.t('Start') }}
      </aha-antd-button>
    </div>
  </aha-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import utils from '@/util'
import {
  IMPORT_OPTIONS, IMPORT_ACTION_OPTIONS, CLOSE_IMPORT_MODAL, START_IMPORTING,
} from '@/constant/eventActions/ImportEventActions.js'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'

// components
import UploadListItem from '@/components-v2/upload/UploadListItem.vue'

// Constants for import options
const OPTIONS_WITH_IMPORT = [IMPORT_OPTIONS.IMPORT_ONLY, IMPORT_OPTIONS.IMPORT_AND_GENERATE]
const OPTIONS_WITH_AI_GENERATION = [IMPORT_OPTIONS.IMPORT_AND_GENERATE, IMPORT_OPTIONS.GENERATE_ONLY]

export default {
  name: 'ImportModalV2',
  components: {
    UploadListItem,
  },
  props: {
    isModalVisible: {
      type: Boolean,
      default: false,
    },
    fileName: {
      type: String,
      default: '',
    },
    isUploadFileCalled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedAction: IMPORT_OPTIONS.IMPORT_AND_GENERATE,
      actionOptions: IMPORT_ACTION_OPTIONS,
      closeButtonTooltipProps: null,
    }
  },
  computed: {
    ...mapState({
      presentationId: (state) => state.presentation?.id,
    }),

    ...mapGetters('aiChatbotImportPptxPdf', [
      'aiChatbotImportSlidesError',
      'aiChatbotUploadedFile',
      'isUploadingFile',
      'sourceFile',
      'canUseAIChatbotImportGenerateSlides',
      'selectedImportOption',
    ]),
    ...mapGetters('rightNav', ['isAITabActive']),

    displayedFileName() {
      return this.aiChatbotUploadedFile?.file_metadata?.original_filename || this.fileName || null
    },

    fileStatus() {
      return utils.getUploadFileStatus(
        this.isUploadFileCalled,
        this.isUploadingFile,
        this.aiChatbotImportSlidesError,
        this.aiChatbotUploadedFile,
      )
    },
  },

  watch: {
    async isUploadingFile(newValue) {
      if (!newValue) return
      this.setCloseButtonTooltipProps()
    },
  },
  methods: {
    ...mapActions('aiChatbotImportPptxPdf', [
      'setIsAIChatbotImportModalVisibleAction',
      'importSlidesAction',
      'setAIChatbotImportSlidesErrorAction',
      'setSourceFileAction',
      'setAIChatbotUploadedFileAction',
      'setIsAIChatbotImportModalVisibleV2Action',
      'setSelectedImportOptionAction',
      'setImportingSlidesForPresentationIdAction',
      'setGeneratingSlidesForPresentationIdAction',
      'processToImportSlidesAction',
    ]),
    ...mapActions(['createNewPresentationAction']),

    trackCloseModal() {
      const eventAction = CLOSE_IMPORT_MODAL
      const eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[this.$router?.currentRoute?.name]
      const trackData = {
        eventAction,
        eventCategory,
        file_name: this.displayedFileName,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    hide() {
      this.$emit('hide')
      this.trackCloseModal()
    },

    selectAction() {
      this.setSelectedImportOptionAction(this.selectedAction)
    },

    removeFile() {
      this.$emit('remove-file')
      this.setAIChatbotUploadedFileAction(null)
      this.setSourceFileAction(null)
    },

    trackStartButton() {
      const eventAction = START_IMPORTING
      const eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[this.$router?.currentRoute?.name]
      const trackData = {
        eventAction,
        eventCategory,
        event_value: this.selectedImportOption,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    setIsImportingOrGeneratingSlides(presentationId) {
      if (!presentationId) return

      const shouldImportSlides = OPTIONS_WITH_IMPORT.includes(this.selectedImportOption)
      const shouldGenerateWithAI = OPTIONS_WITH_AI_GENERATION.includes(this.selectedImportOption)

      if (shouldImportSlides) {
        this.setImportingSlidesForPresentationIdAction(presentationId)
      }
      if (shouldGenerateWithAI) {
        this.setGeneratingSlidesForPresentationIdAction(presentationId)
      }
    },

    handleCreateNewPresentation() {
      this.createNewPresentationAction({
        trigger: 'From Presentation list Import modal V2',
        onSuccess: (newPresentation) => {
          const newPresentationId = newPresentation?.id
          this.setIsImportingOrGeneratingSlides(newPresentationId)
        },
      })
    },

    onStartButtonClick() {
      if (this.selectedImportOption !== this.selectedAction) {
        this.setSelectedImportOptionAction(this.selectedAction)
      }
      this.setIsAIChatbotImportModalVisibleV2Action(false)
      this.trackStartButton()

      if (!this.presentationId) {
        this.handleCreateNewPresentation()
      } else {
        this.setIsImportingOrGeneratingSlides(this.presentationId)
        this.processToImportSlidesAction()
      }
    },

    async setCloseButtonTooltipProps() {
      const TOOLTIP_INITIAL_DELAY_MS = 500
      const TOOLTIP_VISIBLE_DURATION_MS = 500

      // Show tooltip automatically on upload start, then switch to hover trigger
      // since 'manual' trigger cannot be combined with other triggers
      await utils.sleep(TOOLTIP_INITIAL_DELAY_MS)
      this.closeButtonTooltipProps = {
        text: this.$i18nPresenter.t('You can close this pop-up. Importing will continue in the background.'),
        placement: 'top',
        trigger: 'manual',
        visible: true,
        popperOptions: {
          modifiers: {
            preventOverflow: {
              escapeWithReference: true,
            },
          },
        },
      }

      await utils.sleep(TOOLTIP_VISIBLE_DURATION_MS)
      this.closeButtonTooltipProps = {
        ...this.closeButtonTooltipProps,
        trigger: 'mouseenter focus',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.select-modal-upload {
    .file-display-section {
        margin-bottom: 20px;
    }

    .action-selection-section {
        margin-bottom: 32px;
    }

    .action-title {
        font-size: 16px;
        font-weight: 600;
        color: #111827;
        margin-bottom: 16px;
    }

    .action-options {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .option-content {
        display: flex;
        align-items: center;
    }

    .option-icon {
        opacity: 0;
        transition: opacity 0.2s ease;
    }

    .option-icon:focus {
        opacity: 1;
    }

    .action-option:hover .option-icon,
    .action-option:focus .option-icon {
        opacity: 1;
    }

    .modal-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 16px;
    }

    .terms-disclaimer {
        font-size: 12px;
        line-height: 1.4;
    }

    .terms-link,
    .privacy-link {
        color: #6A1EBB;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    .start-button {
        width: 166px;
        // aha-button styles are handled by the component
        // Custom styling can be added here if needed
    }
}
</style>
